/**
 * Write-offs, the faulty register and the shrinkage report.
 *
 * Two different things live here and must not be confused on any screen:
 *
 * **Moving stock to faulty is not a write-off.** The value is still owned and may still be claimed
 * from the supplier. It is one paired movement — sellable out, faulty in — so the shop's total
 * holding does not change, only which bucket it sits in.
 *
 * **A write-off gives the stock up.** It needs owner approval before any stock moves, which is why
 * `requestWriteOff` invalidates the write-off list but not stock: nothing has moved yet.
 * `approveWriteOff` is the call that touches the ledger.
 */

import { api } from '../api.js';
import {
  faultyStockTag,
  movementListTag,
  shrinkageTag,
  stockListTag,
  writeOffListTag,
  writeOffTag,
} from '../tags.js';
import type { Paged, ShopId, VariantId } from '../../types/api.js';
import type {
  FaultyStock,
  ShrinkageReport,
  StockBucket,
  WriteOff,
  WriteOffId,
  WriteOffReason,
  WriteOffReasonCode,
} from '../../types/stockIntegrity.js';

export type ListWriteOffsParams = {
  readonly shopId?: ShopId | undefined;
  /** The approval queue. Everything else is history. */
  readonly onlyPending?: boolean | undefined;
  readonly from?: string | undefined;
  readonly to?: string | undefined;
  readonly page?: number | undefined;
  readonly pageSize?: number | undefined;
};

export type RequestWriteOffBody = {
  readonly shopId?: ShopId | undefined;
  readonly variantId: VariantId;
  /** Serialized stock is written off by unit, so the serial follows the loss. */
  readonly inventoryUnitId?: string | undefined;
  readonly quantity: number;
  readonly reasonCode: WriteOffReasonCode;
  readonly note?: string | undefined;
  /** Defaults to sellable. `faulty` is for giving up on a claim that went nowhere. */
  readonly sourceBucket?: StockBucket | undefined;
};

export type MoveToFaultyBody = {
  readonly shopId?: ShopId | undefined;
  readonly variantId: VariantId;
  readonly quantity: number;
  readonly reason: string;
  /** A phone is moved as the handset itself; quantity is then 1. */
  readonly inventoryUnitId?: string | undefined;
};

export const writeOffsApi = api.injectEndpoints({
  endpoints: (build) => ({
    listWriteOffs: build.query<Paged<WriteOff>, ListWriteOffsParams>({
      query: (params) => ({ url: '/write-offs', params: { page: 1, pageSize: 25, ...params } }),
      providesTags: [writeOffListTag()],
    }),

    /**
     * The reason lookup. Served rather than hardcoded, so adding a reason server-side does not
     * need a dashboard release — and so the shrinkage flag stays the server's business.
     */
    listWriteOffReasons: build.query<WriteOffReason[], void>({
      query: () => '/write-offs/reasons',
      // Effectively static: a seeded lookup table. Held for the session rather than refetched.
      keepUnusedDataFor: 3600,
    }),

    getShrinkageReport: build.query<
      ShrinkageReport,
      { from: string; to: string; shopId?: ShopId | undefined }
    >({
      query: (params) => ({ url: '/write-offs/shrinkage', params }),
      providesTags: [shrinkageTag()],
    }),

    /** Requests only. Nothing moves until an owner approves it. */
    requestWriteOff: build.mutation<WriteOff, RequestWriteOffBody>({
      query: (body) => ({ url: '/write-offs', method: 'POST', body }),
      invalidatesTags: [writeOffListTag()],
    }),

    /** The call that actually removes the stock — except for a transit loss, which already left. */
    approveWriteOff: build.mutation<void, { id: WriteOffId; shopId: ShopId }>({
      query: ({ id }) => ({ url: `/write-offs/${id}/approval`, method: 'POST' }),
      invalidatesTags: (_result, _error, { id, shopId }) => [
        writeOffTag(id),
        writeOffListTag(),
        shrinkageTag(),
        faultyStockTag(shopId),
        stockListTag(),
        movementListTag(),
      ],
    }),

    /** Oldest first from the server, because age is what decides whether a claim is still possible. */
    listFaultyStock: build.query<FaultyStock[], { shopId?: ShopId | undefined }>({
      query: (params) => ({ url: '/faulty-stock', params }),
      providesTags: (_result, _error, { shopId }) =>
        shopId === undefined ? [] : [faultyStockTag(shopId)],
    }),

    /**
     * Sellable out, faulty in, in one transaction. The shop's total holding is unchanged, so this
     * invalidates the stock list because both figures on the row moved — not because the total did.
     */
    moveStockToFaulty: build.mutation<void, MoveToFaultyBody & { shopId: ShopId }>({
      query: (body) => ({ url: '/faulty-stock', method: 'POST', body }),
      invalidatesTags: (_result, _error, { shopId }) => [
        faultyStockTag(shopId),
        stockListTag(),
        movementListTag(),
      ],
    }),

    /** Faulty out, sellable in: marked faulty by mistake, or fixed in the shop. */
    moveFaultyBackOnSale: build.mutation<void, MoveToFaultyBody & { shopId: ShopId }>({
      query: (body) => ({ url: '/faulty-stock/back-on-sale', method: 'POST', body }),
      invalidatesTags: (_result, _error, { shopId }) => [
        faultyStockTag(shopId),
        stockListTag(),
        movementListTag(),
      ],
    }),
  }),
});

export const {
  useMoveFaultyBackOnSaleMutation,
  useListWriteOffsQuery,
  useListWriteOffReasonsQuery,
  useGetShrinkageReportQuery,
  useRequestWriteOffMutation,
  useApproveWriteOffMutation,
  useListFaultyStockQuery,
  useMoveStockToFaultyMutation,
} = writeOffsApi;
